import {
  FLOW_PIECE_LABELS,
  IDEA_ACTIONS,
  IDEA_LABELS,
  ideaText,
  openPieces,
  parseIdeaContent,
  PLAN_IDEA_KINDS,
  type FlowPiece,
  type IdeaContent,
  type IdeaKind,
  type PlanIdeaKind,
  type ServiceFlow,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useServiceAccess } from '../access/useAccess.ts'
import { useApi } from '../api-context.tsx'
import { notify, notifyFailure } from '../feedback.ts'
import {
  aiKeys,
  useAiCost,
  useAiStatus,
  usePlanSermon,
  usePlanSuggestions,
} from './useSuggestions.ts'
import { servicesKeys, usePlan, useSeries } from './useServices.ts'
import type { OrderRef } from './useRunningOrder.ts'
import { sermonKeys } from './sermon-keys.ts'

/**
 * Sermon writing (docs/15-MUSIC-AND-AI.md, "Sermon writing"): a plan's ideas
 * — written from its read sermon, kept, edited before anyone copies or uses
 * them — and the flow suggestions they put on the running order. Nothing is
 * applied by itself: Apply writes a flow piece into its item's description,
 * because a person chose it.
 *
 * Keys live under the plan (`servicesKeys.plan`), so whatever refreshes a
 * plan refreshes its ideas; while one is being written, the query asks
 * again every few seconds.
 */
export const ideaKeys = { plan: sermonKeys.planIdeas }

const POLL_MS = 3000

/** The plan's Editors, where AI is set up: who sees and asks for ideas. */
function useIdeasAllowed(planId: string) {
  const plan = usePlan(planId).data
  const access = useServiceAccess()
  const ai = useAiStatus().data?.enabled === true
  return plan !== undefined && ai && access.can(plan.serviceTypeId, 'editor')
}

export function usePlanIdeas(planId: string, options: { enabled?: boolean } = {}) {
  const api = useApi()
  const allowed = useIdeasAllowed(planId)
  return useQuery({
    queryKey: ideaKeys.plan(planId),
    enabled: planId !== '' && allowed && options.enabled !== false,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).ideas.get()
      if (error) throw error
      return data
    },
    refetchInterval: (query) =>
      Object.values(query.state.data?.ideas ?? {}).some((idea) => idea?.state === 'processing')
        ? POLL_MS
        : false,
  })
}

export type PlanIdeasData = NonNullable<ReturnType<typeof usePlanIdeas>['data']>
export type IdeaView = NonNullable<PlanIdeasData['ideas'][PlanIdeaKind]>

/**
 * What a plan's Series section (and the Sermon page) asks for as the plan
 * opens, so a pill shows it at once: its ideas and its song suggestions,
 * for its Editors.
 */
export function usePlanIdeasPrefetch(planId: string) {
  const allowed = useIdeasAllowed(planId)
  usePlanIdeas(planId, { enabled: allowed })
  usePlanSuggestions(planId, { enabled: allowed })
}

/** One idea's card: what it is, what it costs, where it stands, a glimpse of it. */
export interface IdeaCard<K extends PlanIdeaKind = PlanIdeaKind> {
  kind: K
  title: string
  blurb: string
  idea: IdeaView | null
  content: IdeaContent[K] | null
  /** `none` (never asked for), `processing`, `ready`, `failed`. */
  state: 'none' | 'processing' | 'ready' | 'failed'
  /** A line or two of it, for the card. */
  preview: string | null
  /** "Write · 1 credit", what is left, and why it can't run. */
  cost: ReturnType<typeof useAiCost>
}

/** A glimpse of each kind, for its card. */
function previewOf(kind: PlanIdeaKind, content: IdeaContent[PlanIdeaKind] | null): string | null {
  if (!content) return null
  switch (kind) {
    case 'group_guide':
      return (content as IdeaContent['group_guide']).openingQuestion
    case 'recap':
      return (content as IdeaContent['recap']).newsletter
    case 'family':
      return (content as IdeaContent['family']).kids.explanation
    case 'service_flow': {
      const pieces = (content as IdeaContent['service_flow']).pieces
      const open = pieces.filter((p) => p.appliedAt === null && p.dismissedAt === null)
      const applied = pieces.filter((p) => p.appliedAt !== null).length
      return `${open.length} to look at${applied > 0 ? ` · ${applied} applied` : ''}: ${pieces
        .map((p) => FLOW_PIECE_LABELS[p.kind])
        .filter((label, i, all) => all.indexOf(label) === i)
        .join(', ')}`
    }
    case 'slide_deck': {
      const deck = content as IdeaContent['slide_deck']
      return `${deck.slides.length} slides · ${deck.slides
        .filter((s) => s.kind === 'point')
        .map((s) => s.heading)
        .slice(0, 3)
        .join(' · ')}`
    }
  }
}

/**
 * The plan's ideas as cards, in order, with what each costs; and what the
 * viewer may do. Each card opens its result in a panel (or a full view for
 * the long ones).
 */
export function useIdeaCards(planId: string) {
  const query = usePlanIdeas(planId)
  const data = query.data
  // One useAiCost per kind (hooks in a fixed order).
  const costs = {
    group_guide: useAiCost([IDEA_ACTIONS.group_guide]),
    recap: useAiCost([IDEA_ACTIONS.recap]),
    family: useAiCost([IDEA_ACTIONS.family]),
    service_flow: useAiCost([IDEA_ACTIONS.service_flow]),
    slide_deck: useAiCost([IDEA_ACTIONS.slide_deck]),
  }
  const cards: IdeaCard[] = PLAN_IDEA_KINDS.map((kind) => {
    const idea = data?.ideas[kind] ?? null
    const content = idea ? parseIdeaContent(kind, idea.content) : null
    return {
      kind,
      title: IDEA_LABELS[kind].title,
      blurb: IDEA_LABELS[kind].blurb,
      idea,
      content,
      state: idea ? idea.state : 'none',
      preview: previewOf(kind, content),
      cost: costs[kind],
    }
  })
  return {
    query,
    cards,
    sermonReady: data?.sermonReady === true,
    /** Why nothing can be written yet, in words; null when it can. */
    blocked: !data
      ? null
      : data.sermonReady
        ? null
        : data.sermonState === 'processing'
          ? 'The sermon is being read; ideas can be written once it is.'
          : data.sermonState === 'failed'
            ? 'The sermon couldn’t be read. Read it again, and ideas can be written from it.'
            : 'Add the sermon first: every idea is written from it.',
    slideBackgroundUrl: data?.slideBackgroundUrl ?? null,
  }
}

export function useIdeaActions(planId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: aiKeys.status() })
    return queryClient.invalidateQueries({ queryKey: ideaKeys.plan(planId) })
  }
  const generate = useMutation({
    mutationFn: async (kind: PlanIdeaKind) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).ideas({ kind }).post()
      if (error) throw error
      return data
    },
    onSuccess: () => void refresh(),
    onError: (error, kind) =>
      notifyFailure(
        error,
        undefined,
        `Couldn’t write the ${IDEA_LABELS[kind].title.toLowerCase()}`,
      ),
  })
  const save = useMutation({
    mutationFn: async (input: { kind: PlanIdeaKind; content: IdeaContent[PlanIdeaKind] }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .ideas({ kind: input.kind })
        .put({ content: input.content })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      notify({ tone: 'success', title: 'Saved your changes' })
      void refresh()
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t save it'),
  })
  return { generate, save }
}

/** The whole of an idea as text (Copy all). */
export const ideaAsText = (kind: IdeaKind, content: IdeaContent[IdeaKind]) =>
  ideaText(kind, content)

/**
 * A draft of something written, edited before it is copied or used: starts
 * from what is kept, changes field by field (`update`), and is put away by
 * Cancel or once saved.
 */
export function useDraft<T>(content: T | null) {
  const [draft, setDraft] = useState<T | null>(null)
  return {
    editing: draft !== null,
    /** What shows: the draft while editing, else what is kept. */
    value: draft ?? content,
    draft,
    start: () => setDraft(content === null ? null : structuredClone(content)),
    cancel: () => setDraft(null),
    update: (change: (current: T) => T) =>
      setDraft((current) => (current === null ? current : change(current))),
  }
}

/** A plan idea's draft, saved whole in its kind's shape (refused, and said, when a part is empty). */
export function useIdeaDraft<K extends PlanIdeaKind>(
  planId: string,
  kind: K,
  /** What is kept (read in the kind's shape here). */
  content: IdeaContent[PlanIdeaKind] | null,
) {
  const draft = useDraft<IdeaContent[K]>(content === null ? null : parseIdeaContent(kind, content))
  const actions = useIdeaActions(planId)
  return {
    ...draft,
    pending: actions.save.isPending,
    save: () => {
      if (!draft.draft) return
      const parsed = parseIdeaContent(kind, draft.draft)
      if (!parsed) {
        notify({
          tone: 'error',
          title: 'Something is empty',
          description: 'Every part needs some words: fill it in or remove it.',
        })
        return
      }
      actions.save.mutate({ kind, content: parsed }, { onSuccess: draft.cancel })
    },
  }
}

/** The .pptx download's address (same origin; the session cookie goes with it). */
export const slideDeckUrl = (planId: string) =>
  `/api/v1/services/plans/${planId}/ideas/slide_deck/pptx`

// ---- flow suggestions on the running order -----------------------------------------------------

/**
 * The service flow's suggestions where the running order shows them: by
 * item, still to decide on (not applied, not dismissed). A plan's only, for
 * its Editors, where AI is set up; a template has none.
 */
export function useFlowSuggestions(ref: OrderRef) {
  const planId = ref.kind === 'plan' ? ref.id : ''
  const ideas = usePlanIdeas(planId, { enabled: ref.kind === 'plan' })
  const api = useApi()
  const queryClient = useQueryClient()
  const idea = ideas.data?.ideas.service_flow ?? null
  const flow: ServiceFlow | null =
    idea?.state === 'ready' ? parseIdeaContent('service_flow', idea.content) : null
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: servicesKeys.plan(planId) })
  }
  const apply = useMutation({
    mutationFn: async (input: { piece: FlowPiece; text: string }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .ideas.service_flow.pieces({ pieceId: input.piece.id })
        .apply.post({ text: input.text })
      if (error) throw error
      return data
    },
    onSuccess: (_, input) => {
      notify({
        tone: 'success',
        title: `Added the ${FLOW_PIECE_LABELS[input.piece.kind].toLowerCase()}`,
        description: `It is in “${input.piece.itemTitle}”’s description now.`,
      })
      refresh()
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t apply it'),
  })
  const dismiss = useMutation({
    mutationFn: async (piece: FlowPiece) => {
      const { error } = await api.api.v1.services
        .plans({ id: planId })
        .ideas.service_flow.pieces({ pieceId: piece.id })
        .dismiss.post()
      if (error) throw error
    },
    onSuccess: () => refresh(),
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t put it away'),
  })
  return {
    /** Open suggestions for one item. */
    forItem: (itemId: string) => openPieces(flow, itemId),
    /** How many are open for one item (the row's mark). */
    countFor: (itemId: string) => openPieces(flow, itemId).length,
    all: flow?.pieces ?? [],
    apply,
    dismiss,
    label: (piece: FlowPiece) => FLOW_PIECE_LABELS[piece.kind],
  }
}

export type FlowSuggestions = ReturnType<typeof useFlowSuggestions>

// ---- the hub ------------------------------------------------------------------------------------

/**
 * A plan's sermon hub (its Series section on a phone, its Sermon page on a
 * computer): who may use it — the plan's Editors, where AI is set up — and
 * what its header says: this week in its series ("Week 2 of 4"), the
 * sermon's title and passages, its themes.
 */
export function useSermonHub(plan: {
  id: string
  serviceTypeId: string
  date: string
  title: string | null
  serviceTypeName: string
  series: { id: string; name: string } | null
}) {
  const allowed = useIdeasAllowed(plan.id)
  const sermon = usePlanSermon(plan.id).data?.sermon ?? null
  const series = useSeries(plan.series?.id ?? null).data
  const index = series?.plans.findIndex((p) => p.id === plan.id) ?? -1
  const extracted = sermon?.state === 'ready' ? sermon.extracted : null
  return {
    allowed,
    /** "Week 2 of 4" in its series. */
    week: series && index >= 0 ? `Week ${index + 1} of ${series.plans.length}` : null,
    heading: sermon?.title ?? plan.title ?? plan.serviceTypeName,
    passages: sermon?.passages.length ? sermon.passages : (extracted?.passages ?? []),
    themes: extracted?.themes ?? [],
    day: new Date(`${plan.date}T12:00:00Z`).toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }),
  }
}
